/**
 * Canonical genre list used across the entire app.
 *
 * Each entry has:
 *   id    – the value stored in Firestore (slug-style, lowercase)
 *   label – the human-readable display string shown in the UI
 *   emoji – used in onboarding / filter chips
 *
 * ALWAYS use `normalizeGenre()` when reading genre strings from Firestore so
 * that legacy display-string values ("Hip-Hop", "R&B", "Electronic/EDM", …)
 * are mapped to the canonical label automatically.
 */

export interface GenreEntry {
  id: string;
  label: string;
  emoji: string;
}

export const GENRES: GenreEntry[] = [
  { id: "hip-hop",    label: "Hip-Hop",           emoji: "🎤" },
  { id: "rnb",        label: "R&B / Soul",         emoji: "🎷" },
  { id: "electronic", label: "Electronic",         emoji: "🎛️" },
  { id: "jazz",       label: "Jazz",               emoji: "🎺" },
  { id: "reggae",     label: "Reggae / Dancehall", emoji: "🌴" },
  { id: "rock",       label: "Rock",               emoji: "🎸" },
  { id: "pop",        label: "Pop",                emoji: "🌟" },
  { id: "classical",  label: "Classical",          emoji: "🎻" },
  { id: "gospel",     label: "Gospel",             emoji: "🙏" },
  { id: "afrobeats",  label: "Afrobeats",          emoji: "🥁" },
  { id: "latin",      label: "Latin",              emoji: "💃" },
  { id: "country",    label: "Country / Folk",     emoji: "🤠" },
  { id: "blues",      label: "Blues",              emoji: "🎵" },
  { id: "world",      label: "World Music",        emoji: "🌍" },
  { id: "punk",       label: "Punk / Alternative", emoji: "⚡" },
  { id: "metal",      label: "Metal",              emoji: "🤘" },
  { id: "soul",       label: "Soul",               emoji: "🎶" },
  { id: "funk",       label: "Funk",               emoji: "🕺" },
  { id: "ambient",    label: "Ambient",            emoji: "🌌" },
  { id: "indie",      label: "Indie",              emoji: "🎙️" },
  { id: "folk",       label: "Folk",               emoji: "🪕" },
];

/** id → label */
const ID_TO_LABEL = new Map<string, string>(GENRES.map(g => [g.id, g.label]));

/**
 * Aliases: any legacy / alternate string that should map to a canonical label.
 * Keys are normalised to lowercase before lookup.
 */
const ALIASES: Record<string, string> = {
  // ID variants
  "hip-hop":            "Hip-Hop",
  "hiphop":             "Hip-Hop",
  "hip hop":            "Hip-Hop",
  // Misspelling found in the catalogue (dashboard genre chart, 2026-10-30).
  "cintematic":         "Cinematic",
  "rnb":                "R&B / Soul",
  "r&b":                "R&B / Soul",
  "r&b / soul":         "R&B / Soul",
  "r&b/soul":           "R&B / Soul",
  "electronic":         "Electronic",
  "electronica":        "Electronic",
  "electronic / edm":   "Electronic",
  "electronic/edm":     "Electronic",
  "edm":                "Electronic",
  "reggae":             "Reggae / Dancehall",
  "reggae / dancehall": "Reggae / Dancehall",
  "dancehall":          "Reggae / Dancehall",
  "country":            "Country / Folk",
  "country / folk":     "Country / Folk",
  "folk":               "Folk",
  "gospel":             "Gospel",
  "gospel / spiritual": "Gospel",
  "punk":               "Punk / Alternative",
  "punk / alternative": "Punk / Alternative",
  "alternative":        "Punk / Alternative",
  "alt":                "Punk / Alternative",
  "world":              "World Music",
  "world music":        "World Music",
  "afrobeat":           "Afrobeats",
  "afrobeats":          "Afrobeats",
};

/**
 * Normalise any genre string — ID, alias or already-canonical — to the
 * canonical display label. Returns the input unchanged if no match found.
 */
export function normalizeGenre(raw: string): string {
  if (!raw) return raw;
  const lower = raw.trim().toLowerCase();
  // Direct ID match
  if (ID_TO_LABEL.has(lower)) return ID_TO_LABEL.get(lower)!;
  // Alias match
  if (ALIASES[lower]) return ALIASES[lower];
  // Already a canonical label? Return as-is (case-preserved).
  return raw.trim();
}

/**
 * Normalise an array of genre strings, deduplicating after normalisation.
 */
export function normalizeGenres(raws: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of raws) {
    const norm = normalizeGenre(raw);
    if (norm && !seen.has(norm)) {
      seen.add(norm);
      result.push(norm);
    }
  }
  return result;
}

/** Just the display labels, for <select> / autocomplete lists. */
export const GENRE_LABELS: string[] = GENRES.map(g => g.label);

// ─── Shared DB fetch — used by ALL genre input components ────────────────────

/**
 * Single source-of-truth for genre autocomplete across the entire app.
 *
 * Reads from artists, releases, and works Firestore collections and merges
 * with the canonical GENRE_LABELS list. This ensures every genre input shows
 * the same unified set regardless of which collection the data lives in.
 *
 * Module-level cache so we only hit Firestore once per browser session.
 */
let _genreCache: string[] | null = null;
let _genrePromise: Promise<string[]> | null = null;

export async function fetchAllGenresFromDB(db: import("firebase/firestore").Firestore): Promise<string[]> {
  if (_genreCache) return _genreCache;
  if (_genrePromise) return _genrePromise;

  _genrePromise = (async (): Promise<string[]> => {
    try {
      const { getDocs, collection } = await import("firebase/firestore");
      const set = new Set<string>(GENRE_LABELS); // canonical list is always the base

      const extractGenres = (snap: import("firebase/firestore").QuerySnapshot) => {
        snap.docs.forEach(d => {
          const data = d.data();
          const gs: unknown = data.genres;
          if (Array.isArray(gs)) {
            gs.forEach(g => {
              if (typeof g === "string" && g.trim()) {
                set.add(normalizeGenre(g.trim()));
              }
            });
          }
          const g: unknown = data.genre;
          if (typeof g === "string" && g.trim()) {
            set.add(normalizeGenre(g.trim()));
          }
        });
      };

      const [artistsSnap, releasesSnap, worksSnap] = await Promise.all([
        getDocs(collection(db, "artists")),
        getDocs(collection(db, "releases")),
        getDocs(collection(db, "works")),
      ]);

      extractGenres(artistsSnap);
      extractGenres(releasesSnap);
      extractGenres(worksSnap);

      _genreCache = Array.from(set).filter(Boolean).sort();
      return _genreCache;
    } catch {
      _genreCache = [...GENRE_LABELS];
      return _genreCache;
    }
  })();

  return _genrePromise;
}

/** Invalidate the genre cache (call after adding a new genre to the DB). */
export function invalidateGenreCache(): void {
  _genreCache = null;
  _genrePromise = null;
}
